import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { setAuth } from "../../store/slices/authSlice";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { parseJwt } from "../../lib/utils/jwtToken";
import companyNoParser from "../../lib/utils/companyNoParser";
import { COMPANIES } from "../../types/const";

const ANIMATION_DURATION = 1500; // 1 second for the animation
const REDIRECT_DELAY = 2000; // 1.5 seconds total before redirect

export function AuthInitializer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [company, setCompany] = useState<COMPANIES | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const expires = searchParams.get("expires");
  const azure_token = searchParams.get("azure");
  const azure_refresh_token = searchParams.get("azure_refresh_token");
  const returnState = searchParams.get("state");

  useEffect(() => {
    if (token && expires && azure_token && azure_refresh_token) {
      // First, dispatch the auth action
      const jwtToken = parseJwt(token);
      const typedCompany = companyNoParser(jwtToken.companyno);
      console.log(jwtToken);
      dispatch(
        setAuth({
          token,
          expires,
          azure_token,
          azure_refresh_token,
          company: typedCompany,
          userNo: jwtToken.externaluserno
        })
      );
      setCompany(typedCompany);

      // Clean up URL
      window.history.replaceState({}, "", location.pathname);

      // Start transition
      setIsTransitioning(true);

      // Delay the navigation
      const redirectTimer = setTimeout(() => {
        if (returnState) {
          const decodedState = decodeURIComponent(returnState);
          console.log("Redirecting to:", decodedState);
          navigate(decodedState, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      }, REDIRECT_DELAY);

      return () => clearTimeout(redirectTimer);
    }
  }, [
    dispatch,
    navigate,
    token,
    expires,
    azure_token,
    azure_refresh_token,
    returnState,
    location.pathname,
  ]);

  const getCompanyColor = () => {
    switch (company) {
      case "Enkla":
        return "#FF5F00"; // Enkla orange
      case "Dalakraft":
      default:
        return "#005AA9"; // Dalakraft blue
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Gradient Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, #005AA9 0%, #1E88E5 35%, #FF8F45 65%, #FF5F00 100%)",
          opacity: isTransitioning ? 0 : 1,
          transition: `opacity ${ANIMATION_DURATION}ms ease-in-out`,
        }}
      />

      {/* Company Color Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: getCompanyColor(),
          opacity: isTransitioning ? 1 : 0,
          transition: `opacity ${ANIMATION_DURATION}ms ease-in-out`,
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Stack
          gap={2}
          alignItems="center"
          textAlign="center"
          justifyItems={"center"}
        >
          <CircularProgress
            sx={{
              color: isTransitioning ? "#ffffff" : "primary.main",
              transition: `color ${ANIMATION_DURATION}ms ease-in-out`,
            }}
          />
          <Typography
            sx={{
              color: isTransitioning ? "#ffffff" : "primary.main",
              opacity: isTransitioning ? 1 : 0,
              transition: `opacity ${ANIMATION_DURATION}ms ease-in-out`,
            }}
          >
            Loggar in på {company ?? "..."}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}
